"use client";

import { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { FcGoogle } from "react-icons/fc";
import { FaFacebook, FaWhatsapp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { auth, facebookProvider, googleProvider } from "../../configs/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  getRedirectResult,
  sendPasswordResetEmail,
  signOut,
  UserCredential,
} from "firebase/auth";
import {
  createUserProfile,
  claimPhoneNumberViaBackend,
} from "../../utils/firebaseHelpers";
import { useAuth } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import {
  isDuplicatePhoneError,
  SupportedPhoneCountryCode,
  startWhatsAppAuth,
  verifyWhatsAppAuth,
} from "../../auth/WhatsappAuth/whatsappAuthHelpers";
import WhatsAppPhoneInput from "../../auth/WhatsappAuth/WhatsAppPhoneInput";
import {
  createMetaReviewState,
  buildMetaReviewOAuthUrl,
} from "../MetaReview/metaReviewOAuth";

type ClaimPhoneConflictError = Error & {
  code?: string;
  status?: number;
};

const META_APP_ID = process.env.REACT_APP_META_APP_ID || "";

const META_REVIEW_REDIRECT_URI =
  process.env.REACT_APP_META_REVIEW_REDIRECT_URI ||
  `${window.location.origin}/meta-review/callback`;

const MetaReviewLogin = () => {
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] =
    useState<SupportedPhoneCountryCode>("+265");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSignup, setIsSignup] = useState(false);

  const [isWhatsAppMode, setIsWhatsAppMode] = useState(false);
  const [whatsAppCode, setWhatsAppCode] = useState("");
  const [whatsAppStep, setWhatsAppStep] = useState<"phone" | "code">("phone");
  const [isSendingWhatsAppCode, setIsSendingWhatsAppCode] = useState(false);
  const [isVerifyingWhatsAppCode, setIsVerifyingWhatsAppCode] = useState(false);
  const [whatsAppCodeExpiresAt, setWhatsAppCodeExpiresAt] = useState<
    string | null
  >(null);
  const [whatsAppExistingAccountFound, setWhatsAppExistingAccountFound] =
    useState(false);

  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useCustomTheme();
  const isDark = theme?.name === "dark";

  const dashboardPath = "/meta-review/dashboard";

  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");

    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        if (result?.user) {
          await createUserProfile(result.user);

          toast({
            title: "Facebook Sign-In",
            description: "Login successful!",
            status: "success",
          });

          navigate(dashboardPath, { replace: true });
        }
      })
      .catch((error) => {
        toast({
          title: "Facebook Sign-In Failed",
          description: error.message,
          status: "error",
        });
      });
  }, [toast, navigate]);

  const handleStartMetaPermissionGrant = () => {
    try {
      const state = createMetaReviewState();

      const url = buildMetaReviewOAuthUrl({
        appId: META_APP_ID,
        redirectUri: META_REVIEW_REDIRECT_URI,
        state,
      });

      window.location.assign(url);
    } catch (error) {
      toast({
        title: "Meta permission grant could not start",
        description: (error as Error).message,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const cleanPhoneNumberForClaim = (
    selectedCountryCode: SupportedPhoneCountryCode,
    selectedPhoneNumber: string,
  ) => {
    const cleanCountryCode = selectedCountryCode.replace(/\D/g, "");
    const cleanPhone = selectedPhoneNumber
      .replace(/\D/g, "")
      .replace(/^0+/, "");

    if (!cleanCountryCode || !cleanPhone) {
      return "";
    }

    if (cleanPhone.startsWith(cleanCountryCode)) {
      return cleanPhone;
    }

    return `${cleanCountryCode}${cleanPhone}`;
  };

  const navigateToPhoneUpdatePage = (
    credential: UserCredential,
    attemptedPhoneNumber: string,
  ) => {
    const uid = encodeURIComponent(credential.user.uid);
    const phone = encodeURIComponent(attemptedPhoneNumber);
    const emailParam = encodeURIComponent(credential.user.email || email || "");

    navigate(
      `/complete-phone?uid=${uid}&phone=${phone}&email=${emailParam}&from=signup`,
    );
  };

  const handlePhoneClaimConflict = async (
    credential: UserCredential,
    attemptedPhoneNumber: string,
  ) => {
    try {
      await claimPhoneNumberViaBackend({
        currentUid: credential.user.uid,
        phoneNumber: attemptedPhoneNumber,
      });

      navigate(dashboardPath, { replace: true });
      return;
    } catch (error) {
      const err = error as ClaimPhoneConflictError;

      if (err.code === "PHONE_ALREADY_IN_USE" || err.status === 409) {
        navigateToPhoneUpdatePage(credential, attemptedPhoneNumber);
        return;
      }

      console.error("Phone claim skipped due to network/backend error:", err);

      toast({
        title: "Account created",
        description:
          "Your account was created, but we could not verify the phone number right now.",
        status: "warning",
        duration: 5000,
      });

      navigate(dashboardPath, { replace: true });
    }
  };

  const handleCountryChange = (value: SupportedPhoneCountryCode) => {
    setCountryCode(value);
    setPhoneNumber("");
    setWhatsAppCode("");
    setWhatsAppStep("phone");
    setWhatsAppCodeExpiresAt(null);
    setWhatsAppExistingAccountFound(false);
  };

  const getWhatsAppErrorTitle = (message: string, fallback: string) => {
    if (isDuplicatePhoneError(message)) {
      return "Phone number already linked";
    }

    return fallback;
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isSignup) {
        if (!phoneNumber.trim()) {
          throw new Error("Phone number is required for signup.");
        }

        const fullPhone = cleanPhoneNumberForClaim(countryCode, phoneNumber);

        if (!fullPhone) {
          throw new Error("Please enter a valid phone number.");
        }

        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );

        await createUserProfile(userCredential.user, {
          displayName:
            userCredential.user.displayName || email.split("@")[0] || "",
        });

        await handlePhoneClaimConflict(userCredential, fullPhone);

        toast({
          title: "Account created",
          description: "Welcome to the platform!",
          status: "success",
          duration: 4000,
        });
      } else {
        await signInWithEmailAndPassword(auth, email, password);

        toast({
          title: "Review login successful",
          description: "Welcome to PickDengu Meta App Review.",
          status: "success",
          duration: 4000,
        });

        navigate(dashboardPath, { replace: true });
      }

      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }
    } catch (error) {
      toast({
        title: `${isSignup ? "Signup" : "Login"} failed`,
        description: (error as Error).message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleGoogleAuth = async () => {
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      await createUserProfile(userCredential.user);

      toast({
        title: "Google Sign-In",
        description: "You are now logged in!",
        status: "success",
        duration: 4000,
      });

      navigate(dashboardPath, { replace: true });
    } catch (error) {
      toast({
        title: "Google Sign-In Failed",
        description: (error as Error).message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleFacebookAuth = async () => {
    try {
      const result = await signInWithPopup(auth, facebookProvider);
      await createUserProfile(result.user);

      toast({
        title: "Facebook Sign-In",
        description: "Login successful!",
        status: "success",
      });

      navigate(dashboardPath, { replace: true });
    } catch (error) {
      toast({
        title: "Facebook Sign-In Failed",
        description: (error as Error).message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      toast({
        title: "Enter your email first",
        status: "info",
        duration: 3000,
      });
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      toast({
        title: "Reset email sent",
        description: "Check your inbox to reset your password.",
        status: "success",
        duration: 4000,
      });
    } catch (error) {
      toast({
        title: "Reset failed",
        description: (error as Error).message,
        status: "error",
        duration: 4000,
      });
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      sessionStorage.clear();
      localStorage.removeItem("rememberedEmail");
      navigate("/meta-review", { replace: true });
    } catch (error) {
      toast({
        title: "Logout failed",
        description: (error as Error).message,
        status: "error",
        duration: 4000,
      });
    }
  };

  const handleStartWhatsApp = async () => {
    try {
      if (!phoneNumber.trim()) {
        throw new Error("Please enter your WhatsApp number.");
      }

      setIsSendingWhatsAppCode(true);

      const result = await startWhatsAppAuth({
        countryCode,
        phoneNumber,
        intent: isSignup ? "signup" : "login",
      });

      setWhatsAppCode("");
      setWhatsAppStep("code");
      setWhatsAppCodeExpiresAt(result.expiresAt || null);
      setWhatsAppExistingAccountFound(Boolean(result.existingAccountFound));

      toast({
        title: result.existingAccountFound
          ? "Code sent to existing account"
          : "Code sent",
        description: result.existingAccountFound
          ? "This WhatsApp number is already linked to an account. Enter the latest code to sign in to that account."
          : result?.message ||
            "Check your WhatsApp for the latest verification code.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message || (error as Error).message;

      toast({
        title: getWhatsAppErrorTitle(message, "WhatsApp verification failed"),
        description: message,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsSendingWhatsAppCode(false);
    }
  };

  const handleVerifyWhatsApp = async () => {
    try {
      if (!whatsAppCode.trim()) {
        throw new Error("Please enter the WhatsApp verification code.");
      }

      setIsVerifyingWhatsAppCode(true);

      const result = await verifyWhatsAppAuth({
        countryCode,
        phoneNumber,
        code: whatsAppCode,
      });

      const credential = await signInWithCustomToken(auth, result.customToken);

      console.log("✅ WhatsApp custom token sign-in success:", {
        uid: credential.user.uid,
        requiresProfileCompletion: result.requiresProfileCompletion,
        resultUser: result.user,
      });

      toast({
        title: "WhatsApp login successful",
        description: result.requiresProfileCompletion
          ? "You can complete your profile now or later."
          : "You are now signed in.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      const destination = result.requiresProfileCompletion
        ? "/whatsapp-complete-profile"
        : dashboardPath;

      setTimeout(() => {
        navigate(destination, { replace: true });
      }, 300);
    } catch (error) {
      console.error("❌ WhatsApp verify/login failed:", error);

      const message =
        (error as any)?.response?.data?.message || (error as Error).message;

      toast({
        title: getWhatsAppErrorTitle(message, "Code verification failed"),
        description: message,
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsVerifyingWhatsAppCode(false);
    }
  };

  const cardsBorderRadii = "8px";
  const cardText = isDark ? "rgba(255,255,255,0.92)" : "#0B1220";
  const labelText = isDark ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.75)";
  const subText = isDark ? "rgba(255,255,255,0.65)" : "rgba(15,23,42,0.60)";
  const dividerColor = isDark
    ? "rgba(255,255,255,0.10)"
    : "rgba(15,23,42,0.10)";
  const linkColor = isDark ? "#60A5FA" : "#2563EB";

  const cardShadow = isDark
    ? "0 18px 55px rgba(0,0,0,0.55)"
    : "0 18px 55px rgba(0,0,0,0.16)";

  const cardBg = isDark
    ? "linear-gradient(135deg, rgba(17,24,39,0.92), rgba(2,6,23,0.78), rgba(15,23,42,0.86))"
    : "linear-gradient(to-br, rgba(255,255,255,0.98), rgba(248,250,255,0.92), rgba(255,255,255,0.90))";

  const cardBorder = isDark
    ? "rgba(255,255,255,0.14)"
    : "rgba(255,255,255,0.65)";

  const inputStyles = {
    bg: isDark ? "rgba(2,6,23,0.35)" : "rgba(255,255,255,0.85)",
    color: cardText,
    borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(15,23,42,0.10)",
    _placeholder: {
      color: isDark ? "rgba(255,255,255,0.45)" : "rgba(15,23,42,0.35)",
    },
    _hover: {
      borderColor: isDark ? "rgba(255,255,255,0.26)" : "rgba(15,23,42,0.18)",
    },
    _focus: {
      borderColor: "#3B82F6",
      boxShadow: isDark
        ? "0 0 0 4px rgba(59,130,246,0.22)"
        : "0 0 0 4px rgba(59,130,246,0.15)",
    },
  } as const;

  const primaryAuthButton = isSignup
    ? {
        bgGradient: isDark
          ? "linear(to-r, #319795, #4FD1C5)"
          : "linear(to-r, #38B2AC, #81E6D9)",
        boxShadow: isDark
          ? "0 12px 24px rgba(49,151,149,0.26)"
          : "0 12px 24px rgba(56,178,172,0.20)",
      }
    : {
        bgGradient: isDark
          ? "linear(to-r, #0B1220, #111827, #0B1220)"
          : "linear(to-r, #0F172A, #111827, #0B1220)",
        boxShadow: isDark
          ? "0 12px 24px rgba(0,0,0,0.40)"
          : "0 12px 24px rgba(15,23,42,0.18)",
      };

  return (
    <Container maxW="500px" p={{ base: "0", md: "30px" }}>
      <Box
        p={8}
        borderWidth="1px"
        borderRadius={{ base: "0", md: cardsBorderRadii}}
        boxShadow={cardShadow}
        bgGradient={cardBg}
        borderColor={cardBorder}
        backdropFilter="blur(14px)"
        color={cardText}
        minH={{ base: "100vh", md: "100%" }}
        overflow="hidden"
        position="relative"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="5px"
          opacity={0.95}
        />

        <VStack spacing={6} position="relative">
          <Box
            width="100%"
            p={4}
            borderWidth="1px"
            borderColor={
              isDark ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.10)"
            }
            borderRadius={cardsBorderRadii}
            bg={isDark ? "rgba(15,23,42,0.58)" : "rgba(239,246,255,0.90)"}
          >
            <Stack spacing={3}>
              <Badge alignSelf="flex-start" colorScheme="blue">
                Meta App Review
              </Badge>

              <Heading size="sm" color={cardText}>
                PickDengu Reviewer Login
              </Heading>

              <Text fontSize="sm" color={subText}>
                First grant the requested Meta permissions, then sign in with
                the reviewer account to open the PickDengu Meta Review
                dashboard.
              </Text>

              <Tooltip label="Opens Meta Login so the reviewer can grant whatsapp_business_management, whatsapp_business_messaging, and catalog_management.">
                <Button
                  bg="#1877F2"
                  width="full"
                  borderRadius={cardsBorderRadii}
                  onClick={handleStartMetaPermissionGrant}
                >
                  Start Meta Permission Grant
                </Button>
              </Tooltip>

              <Text fontSize="xs" color={subText}>
                Caption for reviewer: This button demonstrates the complete
                Meta/Facebook consent flow for the requested App Review
                permissions.
              </Text>
            </Stack>
          </Box>

          <Heading width="100%" textAlign="left" size="lg" color={cardText}>
            {isWhatsAppMode
              ? whatsAppStep === "phone"
                ? "Continue with WhatsApp"
                : "Enter WhatsApp Code"
              : isSignup
                ? "Create an Account"
                : "Sign In for Meta Review"}
          </Heading>

          {!isWhatsAppMode ? (
            <form onSubmit={handleAuth} style={{ width: "100%" }}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color={labelText}>Email address</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    {...inputStyles}
                  />
                </FormControl>

                {isSignup && (
                  <WhatsAppPhoneInput
                    isRequired
                    label="Phone number"
                    countryCode={countryCode}
                    phoneNumber={phoneNumber}
                    onCountryCodeChange={handleCountryChange}
                    onPhoneNumberChange={setPhoneNumber}
                    labelColor={labelText}
                    inputStyles={inputStyles}
                  />
                )}

                <FormControl isRequired>
                  <FormLabel color={labelText}>Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      {...inputStyles}
                    />
                    <InputRightElement h="full">
                      <IconButton
                        variant="ghost"
                        aria-label="Toggle password"
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                {!isSignup && (
                  <HStack justifyContent="space-between">
                    <Checkbox
                      isChecked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      colorScheme="blue"
                    >
                      Remember me
                    </Checkbox>
                    <Link
                      color={linkColor}
                      fontSize="sm"
                      onClick={handleResetPassword}
                    >
                      Forgot password?
                    </Link>
                  </HStack>
                )}

                <Button
                  {...primaryAuthButton}
                  _hover={{
                    filter: "brightness(1.06)",
                    transform: "translateY(-1px)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  fontWeight="bold"
                  color="white"
                  type="submit"
                  width="full"
                  borderRadius={cardsBorderRadii}
                >
                  {isSignup ? "Sign Up" : "Sign In"}
                </Button>
              </Stack>
            </form>
          ) : (
            <Stack spacing={4} width="100%">
              <WhatsAppPhoneInput
                isRequired
                label="WhatsApp number"
                countryCode={countryCode}
                phoneNumber={phoneNumber}
                onCountryCodeChange={handleCountryChange}
                onPhoneNumberChange={setPhoneNumber}
                labelColor={labelText}
                inputStyles={inputStyles}
              />

              {whatsAppStep === "code" && (
                <>
                  <FormControl isRequired>
                    <FormLabel color={labelText}>Verification code</FormLabel>
                    <Input
                      type="text"
                      value={whatsAppCode}
                      onChange={(e) =>
                        setWhatsAppCode(
                          e.target.value.replace(/\D/g, "").slice(0, 6),
                        )
                      }
                      placeholder="Enter latest 6-digit code"
                      {...inputStyles}
                    />
                  </FormControl>

                  <Stack spacing={1}>
                    {whatsAppExistingAccountFound && (
                      <Text fontSize="sm" color={subText}>
                        This number is already linked to an account. Enter the
                        latest code to sign in to that account.
                      </Text>
                    )}

                    {whatsAppCodeExpiresAt && (
                      <Text fontSize="xs" color={subText}>
                        Code expires at{" "}
                        {new Date(whatsAppCodeExpiresAt).toLocaleTimeString(
                          [],
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                        .
                      </Text>
                    )}
                  </Stack>
                </>
              )}

              {whatsAppStep === "phone" ? (
                <Button
                  leftIcon={<Icon color="teal.700" as={FaWhatsapp as any} />}
                  colorScheme="whatsapp"
                  onClick={handleStartWhatsApp}
                  isLoading={isSendingWhatsAppCode}
                  isDisabled={
                    isSendingWhatsAppCode ||
                    isVerifyingWhatsAppCode ||
                    !phoneNumber.trim()
                  }
                  borderRadius="5px"
                  color={cardText}
                  variant="outline"
                >
                  Send WhatsApp Code
                </Button>
              ) : (
                <Stack spacing={3}>
                  <Button
                    leftIcon={<Icon color="teal.600" as={FaWhatsapp as any} />}
                    colorScheme="whatsapp"
                    onClick={handleVerifyWhatsApp}
                    isLoading={isVerifyingWhatsAppCode}
                    isDisabled={
                      isSendingWhatsAppCode ||
                      isVerifyingWhatsAppCode ||
                      whatsAppCode.length !== 6
                    }
                    borderRadius="5px"
                    color={cardText}
                    variant="outline"
                  >
                    Verify and Sign In
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleStartWhatsApp}
                    isLoading={isSendingWhatsAppCode}
                    isDisabled={
                      isSendingWhatsAppCode || isVerifyingWhatsAppCode
                    }
                    color={linkColor}
                  >
                    Send New WhatsApp Code
                  </Button>
                </Stack>
              )}

              <Button
                variant="ghost"
                onClick={() => {
                  if (whatsAppStep === "code") {
                    setWhatsAppStep("phone");
                    setWhatsAppCode("");
                    setWhatsAppCodeExpiresAt(null);
                    setWhatsAppExistingAccountFound(false);
                    return;
                  }

                  setIsWhatsAppMode(false);
                }}
              >
                Back
              </Button>
            </Stack>
          )}

          {!isWhatsAppMode && (
            <Text
              color={isDark ? "rgba(255,255,255,0.70)" : "rgba(15,23,42,0.75)"}
            >
              {isSignup ? "Already have an account?" : "Don't have an account?"}{" "}
              <Button
                onClick={() => setIsSignup(!isSignup)}
                variant="link"
                color={linkColor}
                size="sm"
              >
                {isSignup ? "Sign In" : "Sign Up"}
              </Button>
            </Text>
          )}

          <Divider borderColor={dividerColor} />
          {!isWhatsAppMode && <Text color={subText}>or continue with</Text>}

          <Stack spacing={4} width="100%">
            {!isWhatsAppMode && (
              <>
                <Button
                  leftIcon={
                    <Icon
                      as={FaWhatsapp as any}
                      boxSize={5}
                      borderRadius={cardsBorderRadii}
                      color="#1F8F4D"
                    />
                  }
                  variant="outline"
                  bg="transparent"
                  color="#1F8F4D"
                  border="1px solid"
                  borderColor="#1F8F4D"
                  h="40px"
                  minH="40px"
                  px={4}
                  fontWeight="600"
                  borderRadius={cardsBorderRadii}
                  _hover={{
                    bg: "rgba(31, 143, 77, 0.06)",
                    borderColor: "#187A41",
                    color: "#187A41",
                  }}
                  _active={{
                    bg: "rgba(31, 143, 77, 0.10)",
                    borderColor: "#146B39",
                    color: "#146B39",
                  }}
                  onClick={() => {
                    setIsWhatsAppMode(true);
                    setWhatsAppStep("phone");
                    setWhatsAppCode("");
                    setWhatsAppCodeExpiresAt(null);
                    setWhatsAppExistingAccountFound(false);
                    setCountryCode("+265");
                    setPhoneNumber("");
                  }}
                >
                  Continue with WhatsApp
                </Button>

                <Button
                  variant="outline"
                  leftIcon={FcGoogle({}) as React.ReactElement}
                  onClick={handleGoogleAuth}
                >
                  Continue with Google
                </Button>

                <Button onClick={handleFacebookAuth}>
                  <Icon
                    as={FaFacebook as React.ElementType}
                    boxSize={4}
                    mr="8px"
                    color="#FFF"
                  />
                  Continue with Facebook
                </Button>
              </>
            )}

            {user ? (
              <Button variant="ghost" onClick={handleLogout}>
                Logout
              </Button>
            ) : null}
          </Stack>
        </VStack>
      </Box>
    </Container>
  );
};

export default MetaReviewLogin;
